/**
 * `useTypewriterText` — buffered character-by-character playback of streaming text.
 *
 * SSE deltas from the chat backend arrive in chunks of varying size — 1-50 chars
 * at a time depending on the model's tokenization. Rendering them raw produces a
 * jumpy "clumps appear out of order" UX. This hook buffers the incoming deltas
 * and reveals the target text one character at a time on a rAF loop, with a
 * catch-up term that keeps the visible cursor 1-2 seconds behind the backend's
 * incoming buffer even when the model streams quickly.
 *
 * The visible cursor (the caller renders a blinking cursor when `isCatchingUp`)
 * makes the playback feel like the search-preview typewriter elsewhere in the
 * chat surface.
 *
 * Behaviour:
 *   - When `targetText` grows, the visible text catches up at a smooth pace.
 *   - When `targetText` shrinks (e.g. a phase-clear event wipes the buffer),
 *     the visible text resets to match — no animation backward.
 *   - When `targetText` is empty, the visible text is empty (no cursor).
 *   - When visible == target, the cursor stops blinking.
 */

import { useEffect, useRef, useState } from 'react';

/** Base character-per-second rate when the backend is idle. ~120 cps = ~7200
 *  chars per minute; comfortable reading pace, faster than a human can read
 *  word-by-word. */
const TYPEWRITER_BASE_CPS = 120;

/** Per-frame catch-up fraction: how much of the remaining gap to consume each
 *  frame on TOP of the base rate. 0.08 at 60 fps means a 1000-char gap closes
 *  in ~1.5 seconds; for typical streaming where the gap is 20-100 chars, the
 *  cursor stays within ~1s of the backend. */
const TYPEWRITER_CATCHUP_FACTOR = 0.08;

export interface TypewriterTextResult {
  /** The portion of `targetText` revealed so far. The chat bubble renders THIS. */
  visibleText: string;
  /** True while `visibleText.length < targetText.length` — caller can show a
   *  blinking cursor at the end of the visible text. */
  isCatchingUp: boolean;
}

/**
 * Buffer the target text and reveal it character-by-character. Returns the
 * currently visible substring plus a flag indicating whether the typewriter
 * is still catching up.
 */
export function useTypewriterText(targetText: string): TypewriterTextResult {
  const [visibleText, setVisibleText] = useState('');
  const targetRef = useRef(targetText);
  const lastFrameTimeRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  // Keep the target ref in sync without re-running the rAF effect on every
  // delta — the loop reads `targetRef.current` each frame.
  targetRef.current = targetText;

  useEffect(() => {
    let cancelled = false;
    lastFrameTimeRef.current = null;

    const tick = (now: number) => {
      if (cancelled) return;
      const lastTime = lastFrameTimeRef.current;
      const dt = lastTime === null ? 16 : now - lastTime;
      lastFrameTimeRef.current = now;

      setVisibleText((prev) => {
        const target = targetRef.current;
        if (target.length === 0) return '';
        // Target shrunk (phase clear or new turn) — snap to match without
        // animating "backward" (which would look broken).
        if (prev.length > target.length) return target;
        if (prev.length === target.length) return prev;
        const gap = target.length - prev.length;
        // Base reveal: cps * elapsed seconds = chars per frame at the steady rate.
        const baseReveal = (TYPEWRITER_BASE_CPS * dt) / 1000;
        // Catch-up term: extra characters proportional to remaining gap, so the
        // typewriter stays close to the backend stream when it bursts.
        const catchup = gap * TYPEWRITER_CATCHUP_FACTOR;
        const reveal = Math.max(1, Math.ceil(baseReveal + catchup));
        return target.slice(0, prev.length + reveal);
      });

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, []);

  return {
    visibleText,
    isCatchingUp: visibleText.length < targetText.length,
  };
}
